// Renders the OCR text layer for the current page.
//
// The sudhan backend's `pages[<n>]` shape isn't strictly typed — different
// pipeline versions surface the OCR text in different fields. We probe the
// likely candidates and gracefully degrade to a "no text" message when
// nothing matches, so the cockpit's "OCR text" view still renders something
// useful instead of a blank panel.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, FileText, Loader2, AlertCircle } from "lucide-react";
import { ocrApi } from "../../../services/ocrApi";

interface OcrTextPanelProps {
  /** The `pages[<currentPage>]` blob from the verification_home response. */
  pageData: unknown;
  /** Page number — used in the empty-state copy. */
  currentPage: number;
  /** Process ID — used to call export_json API for OCR text. */
  processId?: string;
}

interface TextLine {
  text: string;
  confidence?: number;
}

/**
 * Colorful JSON viewer component with syntax highlighting.
 */
function JsonViewer({ data }: { data: unknown }) {
  const renderValue = (value: unknown, depth = 0): React.ReactNode => {
    if (value === null) {
      return <span className="text-gray-500">null</span>;
    }
    if (typeof value === "boolean") {
      return <span className="text-blue-600 font-semibold">{String(value)}</span>;
    }
    if (typeof value === "number") {
      return <span className="text-orange-600 font-semibold">{value}</span>;
    }
    if (typeof value === "string") {
      return <span className="text-green-600">"{value}"</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-gray-700">[]</span>;
      return (
        <div className="ml-4">
          <span className="text-gray-700">[</span>
          <div className="ml-2">
            {value.map((item, i) => (
              <div key={i} className="text-gray-800">
                {renderValue(item, depth + 1)}
                {i < value.length - 1 && <span className="text-gray-700">,</span>}
              </div>
            ))}
          </div>
          <span className="text-gray-700">]</span>
        </div>
      );
    }
    if (typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) return <span className="text-gray-700">{"{}"}</span>;
      return (
        <div className="ml-4">
          <span className="text-gray-700">{"{"}</span>
          <div className="ml-2">
            {entries.map(([key, val], i) => (
              <div key={key} className="text-gray-800">
                <span className="text-purple-600">"{key}"</span>
                <span className="text-gray-700">: </span>
                {renderValue(val, depth + 1)}
                {i < entries.length - 1 && <span className="text-gray-700">,</span>}
              </div>
            ))}
          </div>
          <span className="text-gray-700">{"}"}</span>
        </div>
      );
    }
    return <span className="text-gray-700">{String(value)}</span>;
  };

  return (
    <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
      {renderValue(data)}
    </pre>
  );
}

/**
 * Best-effort extraction of OCR text from whatever shape the backend ships.
 *
 * Probes (in order):
 *   1. `pageData.text` / `pageData.full_text` (string) → split on \n
 *   2. `pageData.lines[]`        (array of `{ text }` objects)
 *   3. `pageData.words[]`        (array of `{ text }` objects, joined into lines on y)
 *   4. `pageData.ocr.text` / `pageData.ocr.lines` (nested under an `ocr` key)
 *   5. Flat JSON object (key-value pairs) → format as "key: value" lines
 *
 * Returns `[]` when nothing matches so the empty state is hit.
 */
function extractLines(pageData: unknown): TextLine[] {
  if (!pageData || typeof pageData !== "object") return [];
  const obj = pageData as Record<string, unknown>;

  // 1. Plain text
  const textCandidates = [obj.text, obj.full_text, obj.fullText, obj.ocr_text];
  for (const t of textCandidates) {
    if (typeof t === "string" && t.trim().length > 0) {
      return t.split(/\r?\n/).map((line) => ({ text: line }));
    }
  }

  // Generic helper: pull text from an array of word/line objects.
  const fromArray = (arr: unknown[]): TextLine[] => {
    const lines: TextLine[] = [];
    for (const item of arr) {
      if (!item || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const t = rec.text ?? rec.value ?? rec.content;
      if (typeof t === "string" && t.length > 0) {
        const conf =
          typeof rec.confidence === "number"
            ? rec.confidence
            : typeof rec.score === "number"
            ? rec.score
            : undefined;
        lines.push({ text: t, confidence: conf });
      }
    }
    return lines;
  };

  // 2. Lines array (preferred over words — already grouped)
  if (Array.isArray(obj.lines)) {
    const fromLines = fromArray(obj.lines as unknown[]);
    if (fromLines.length > 0) return fromLines;
  }

  // 3. Words array (less ideal — won't preserve layout but better than nothing)
  if (Array.isArray(obj.words)) {
    const fromWords = fromArray(obj.words as unknown[]);
    if (fromWords.length > 0) {
      // Collapse all words into a single space-joined line.
      return [{ text: fromWords.map((l) => l.text).join(" ") }];
    }
  }

  // 4. Nested under `ocr`
  if (obj.ocr && typeof obj.ocr === "object") {
    return extractLines(obj.ocr);
  }

  // 5. Flat JSON object with key-value pairs (e.g., export_json response)
  // Format as "key: value" lines for display
  const lines: TextLine[] = [];
  const flattenObject = (o: Record<string, unknown>, prefix = "") => {
    for (const [key, value] of Object.entries(o)) {
      if (value === null || value === undefined) continue;
      
      if (typeof value === "object" && !Array.isArray(value)) {
        // Recurse into nested objects
        flattenObject(value as Record<string, unknown>, prefix ? `${prefix}.${key}` : key);
      } else if (Array.isArray(value)) {
        // Skip arrays for now
        continue;
      } else {
        // Primitive value — format as "key: value"
        const displayKey = prefix ? `${prefix}.${key}` : key;
        const displayValue = String(value).trim();
        if (displayValue.length > 0) {
          lines.push({ text: `${displayKey}: ${displayValue}` });
        }
      }
    }
  };
  
  flattenObject(obj);
  if (lines.length > 0) return lines;

  return [];
}

export function OcrTextPanel({ pageData, currentPage, processId }: OcrTextPanelProps) {
  const [filter, setFilter] = useState("");

  const exportQuery = useQuery({
    queryKey: ["ocr", "export_json", processId],
    queryFn: () => ocrApi.exportJson(processId!),
    enabled: !!processId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Use API response first; fall back to pageData text extraction
  const lines = useMemo(() => {
    if (exportQuery.data) {
      const fromApi = extractLines(exportQuery.data);
      if (fromApi.length > 0) return fromApi;
    }
    return extractLines(pageData);
  }, [exportQuery.data, pageData]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return lines;
    const q = filter.toLowerCase();
    return lines.filter((l) => l.text.toLowerCase().includes(q));
  }, [lines, filter]);

  if (exportQuery.isLoading) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        <p className="text-sm font-medium text-gray-700">Loading OCR text…</p>
      </div>
    );
  }

  if (exportQuery.isError) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-red-200 bg-white p-8 text-center">
        <AlertCircle className="h-6 w-6 text-red-400" />
        <p className="text-sm font-medium text-gray-700">Failed to load OCR text</p>
        <p className="text-xs text-gray-500">
          {exportQuery.error instanceof Error ? exportQuery.error.message : "Unknown error"}
        </p>
        <button
          onClick={() => exportQuery.refetch()}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Prefer showing JSON if API data exists; otherwise show text lines
  const showJsonViewer = !!exportQuery.data;

  if (!showJsonViewer && lines.length === 0) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        <FileText className="h-6 w-6 text-gray-400" />
        <p className="font-medium text-gray-700">
          No OCR text available for page {currentPage}
        </p>
        <p className="text-xs text-gray-500">
          The backend's response for this page didn't include a text layer.
          Try the Document or Original view instead.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full max-w-3xl flex-col rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-2">
        <FileText className="h-4 w-4 text-blue-600" />
        <h3 className="text-sm font-semibold text-gray-900">
          {showJsonViewer ? "Response JSON" : "OCR text"} — page {currentPage}
        </h3>
        {!showJsonViewer && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
            {lines.length} line{lines.length === 1 ? "" : "s"}
          </span>
        )}
        {!showJsonViewer && (
          <div className="ml-auto relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find in text…"
              className="w-56 rounded-md border border-gray-300 bg-white py-1 pl-7 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto px-4 py-3">
        {showJsonViewer ? (
          <JsonViewer data={exportQuery.data} />
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-xs text-gray-500">
            No lines match "{filter}".
          </p>
        ) : (
          <ol className="space-y-0.5 font-mono text-xs leading-relaxed text-gray-800">
            {filtered.map((line, i) => (
              <li key={i} className="flex items-baseline gap-3">
                <span className="w-8 shrink-0 select-none text-right tabular-nums text-gray-400">
                  {i + 1}
                </span>
                <span className="whitespace-pre-wrap wrap-break-word">
                  {line.text}
                </span>
                {line.confidence != null ? (
                  <span
                    className="ml-auto shrink-0 tabular-nums text-[10px] text-gray-400"
                    title="OCR confidence"
                  >
                    {(line.confidence * 100).toFixed(0)}%
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
